import React, { useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const normalizeBase = (u) => (u || "").replace(/\/+$/, "");

function ScoreCard({ s }) {
    return (
        <div
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 16,
                background: "#ffffff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>{s.category}</div>
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1d4ed8",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 999,
                        padding: "6px 10px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {s.rawPoints} / {s.maxPoints}
                </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
                {s.feedback}
            </div>

            {Array.isArray(s.strengths) && s.strengths.length > 0 && (
                <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#065f46", marginBottom: 6 }}>
                        Strengths
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#374151", lineHeight: 1.5 }}>
                        {s.strengths.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </div>
            )}

            {Array.isArray(s.improvements) && s.improvements.length > 0 && (
                <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 6 }}>
                        Improvements
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#374151", lineHeight: 1.5 }}>
                        {s.improvements.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default function CourseAuditWidget({ backEndUrl, tenantId }) {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = query.get("token") || "";

    const [courseZip, setCourseZip] = useState(null);
    const [rubricPdf, setRubricPdf] = useState(null);
    const [extraInstructions, setExtraInstructions] = useState("");

    const [busy, setBusy] = useState(false);
    const [statusText, setStatusText] = useState("");
    const [error, setError] = useState("");
    const [payload, setPayload] = useState(null);

    const defaultRubricPdfUrl =
        "https://anthologyinc-my.sharepoint.com/:b:/g/personal/john_wiseman_anthology_com/IQBSxOYaGs8wRKd_1N6u_PluAaDAKmkDCGzPkY6cN5lHBnQ?e=ZKMegx";

    const canSubmit = !!(courseZip && rubricPdf && token && backEndUrl) && !busy;

    async function runAudit() {
        setError("");
        setStatusText("");
        if (!canSubmit) return;

        try {
            setBusy(true);
            setPayload(null);

            const base = normalizeBase(backEndUrl);

            if (!String(courseZip?.name || "").toLowerCase().endsWith(".zip")) {
                throw new Error("Please select a .zip file for the Blackboard course export.");
            }
            if (!String(rubricPdf?.name || "").toLowerCase().endsWith(".pdf")) {
                throw new Error("Please select a .pdf file for the rubric.");
            }

            setStatusText("Requesting secure upload URLs...");

            const { data: presign } = await axios.post(
                `${base}/course-audit/upload-urls`,
                {
                    tenantId,
                    courseZipFileName: courseZip.name,
                    courseZipSize: courseZip.size,
                    courseZipContentType: courseZip.type || "application/zip",
                    rubricPdfFileName: rubricPdf.name,
                    rubricPdfSize: rubricPdf.size,
                    rubricPdfContentType: rubricPdf.type || "application/pdf",
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                    },
                    timeout: 60000,
                }
            );

            const auditId = presign?.auditId;
            const zipUpload = presign?.uploads?.courseZip;
            const pdfUpload = presign?.uploads?.rubricPdf;

            if (!auditId || !zipUpload?.uploadUrl || !pdfUpload?.uploadUrl) {
                throw new Error("Upload URL response was incomplete.");
            }

            setStatusText("Uploading course ZIP to storage...");
            await axios.put(zipUpload.uploadUrl, courseZip, {
                headers: {
                    "Content-Type": zipUpload.contentType || courseZip.type || "application/zip",
                },
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
                timeout: 30 * 60 * 1000,
            });

            setStatusText("Uploading rubric PDF to storage...");
            await axios.put(pdfUpload.uploadUrl, rubricPdf, {
                headers: {
                    "Content-Type": pdfUpload.contentType || rubricPdf.type || "application/pdf",
                },
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
                timeout: 10 * 60 * 1000,
            });

            setStatusText("Queueing audit...");
            const { data: queued } = await axios.post(
                `${base}/course-audit/analyze`,
                {
                    tenantId,
                    auditId,
                    zipKey: zipUpload.key,
                    pdfKey: pdfUpload.key,
                    auditMode: "WITHOUT_ENROLLMENTS",
                    extraInstructions: extraInstructions || "",
                    courseZipFileName: courseZip.name,
                    rubricPdfFileName: rubricPdf.name,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                    },
                    timeout: 60000,
                }
            );

            const queuedAuditId = queued?.auditId || auditId;

            setStatusText(`Queued (Audit ID: ${queuedAuditId}). Waiting for worker...`);

            const maxPolls = 180;
            for (let i = 0; i < maxPolls; i++) {
                const { data: status } = await axios.get(
                    `${base}/course-audit/status/${encodeURIComponent(queuedAuditId)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                        },
                        timeout: 60000,
                    }
                );

                const currentStatus = status?.status || "PROCESSING";
                setStatusText(`Status: ${currentStatus}...`);

                if (currentStatus === "COMPLETED") {
                    setPayload({
                        auditId: queuedAuditId,
                        metadata: status.metadata,
                        result: status.result,
                    });
                    setStatusText("Audit completed.");
                    return;
                }

                if (currentStatus === "FAILED") {
                    throw new Error(status?.error || "Audit failed in background worker.");
                }

                await new Promise((r) => setTimeout(r, 5000));
            }

            throw new Error("Audit is still processing. Please try again in a few minutes.");
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Audit failed.");
            setStatusText("");
        } finally {
            setBusy(false);
        }
    }

    function downloadJson() {
        if (!payload?.result) return;
        const blob = new Blob([JSON.stringify(payload.result, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${payload?.result?.metadata?.courseTitle || "course-audit"}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }

    function addWrappedText(doc, text, x, y, maxWidth, lineHeight = 14) {
        const lines = doc.splitTextToSize(String(text || ""), maxWidth);
        let yy = y;
        lines.forEach((line) => {
            doc.text(line, x, yy);
            yy += lineHeight;
        });
        return yy;
    }

    function downloadPdf() {
        const result = payload?.result;
        if (!result) return;

        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const contentWidth = pageWidth - margin * 2;

        const courseTitle =
            result?.metadata?.courseTitle ||
            payload?.metadata?.courseTitle ||
            "Course Audit Report";

        const safeFileName = String(courseTitle)
            .replace(/[\\/:*?"<>|]+/g, "_")
            .slice(0, 120);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("Course Audit Report (ECP Rubric)", margin, 40);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);

        let y = 64;
        y = addWrappedText(doc, `Course: ${courseTitle}`, margin, y, contentWidth);
        y = addWrappedText(doc, `Audit ID: ${payload?.auditId || "(unknown)"}`, margin, y, contentWidth);
        y = addWrappedText(
            doc,
            `Score: ${result?.totalPoints ?? 0} / ${result?.maxPossiblePoints ?? 191} (${result?.percentage ?? 0}%)`,
            margin,
            y,
            contentWidth
        );
        y = addWrappedText(doc, `Rating: ${result?.ratingLabel || "N/A"}`, margin, y, contentWidth);
        y += 8;

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["Metric", "Value"]],
            body: [
                ["Total Points", `${result?.totalPoints ?? 0}`],
                ["Max Points", `${result?.maxPossiblePoints ?? 191}`],
                ["Percentage", `${result?.percentage ?? 0}%`],
                ["Rating", `${result?.ratingLabel || "N/A"}`],
            ],
            theme: "grid",
            styles: { fontSize: 10, cellPadding: 6, overflow: "linebreak" },
            headStyles: { fontStyle: "bold" },
            columnStyles: {
                0: { cellWidth: 140, fontStyle: "bold" },
                1: { cellWidth: "auto" },
            },
        });

        y = doc.lastAutoTable.finalY + 14;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Overall Feedback", margin, y);
        y += 10;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        y = addWrappedText(
            doc,
            result?.overallFeedback || "No overall feedback provided.",
            margin,
            y + 8,
            contentWidth,
            13
        );
        y += 8;

        const scoreRows = (result?.scores || []).map((s) => [
            s?.category || "",
            `${s?.rawPoints ?? 0}`,
            `${s?.maxPoints ?? 0}`,
            s?.maxPoints ? `${Math.round(((s.rawPoints || 0) / s.maxPoints) * 100)}%` : "",
        ]);

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["Category", "Points", "Max", "%"]],
            body: scoreRows.length ? scoreRows : [["(No category scores)", "", "", ""]],
            theme: "striped",
            styles: { fontSize: 10, cellPadding: 5, overflow: "linebreak", valign: "top" },
            headStyles: { fontStyle: "bold" },
            columnStyles: {
                0: { cellWidth: 260 },
                1: { cellWidth: 60, halign: "right" },
                2: { cellWidth: 60, halign: "right" },
                3: { cellWidth: 60, halign: "right" },
            },
        });

        y = doc.lastAutoTable.finalY + 16;

        (result?.scores || []).forEach((s, idx) => {
            if (y > pageHeight - 180) {
                doc.addPage();
                y = margin;
            }

            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            y = addWrappedText(
                doc,
                `${idx + 1}. ${s?.category || "Category"} (${s?.rawPoints ?? 0}/${s?.maxPoints ?? 0})`,
                margin,
                y,
                contentWidth,
                14
            );

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);

            y = addWrappedText(doc, `Feedback: ${s?.feedback || ""}`, margin + 8, y + 4, contentWidth - 8, 13);

            if (Array.isArray(s?.strengths) && s.strengths.length) {
                doc.setFont("helvetica", "bold");
                y = addWrappedText(doc, "Strengths", margin + 8, y + 6, contentWidth - 8, 13);
                doc.setFont("helvetica", "normal");
                s.strengths.forEach((item) => {
                    y = addWrappedText(doc, `• ${item}`, margin + 20, y + 2, contentWidth - 20, 13);
                    if (y > pageHeight - 70) {
                        doc.addPage();
                        y = margin;
                    }
                });
            }

            if (Array.isArray(s?.improvements) && s.improvements.length) {
                doc.setFont("helvetica", "bold");
                y = addWrappedText(doc, "Improvements", margin + 8, y + 6, contentWidth - 8, 13);
                doc.setFont("helvetica", "normal");
                s.improvements.forEach((item) => {
                    y = addWrappedText(doc, `• ${item}`, margin + 20, y + 2, contentWidth - 20, 13);
                    if (y > pageHeight - 70) {
                        doc.addPage();
                        y = margin;
                    }
                });
            }

            y += 10;
        });

        if (result?.enrollmentInsights) {
            if (y > pageHeight - 150) {
                doc.addPage();
                y = margin;
            }

            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Enrollment Insights", margin, y);
            y += 12;

            const insightsRows = Object.entries(result.enrollmentInsights).map(([k, v]) => [
                k,
                typeof v === "string" ? v : JSON.stringify(v, null, 2),
            ]);

            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [["Field", "Value"]],
                body: insightsRows,
                theme: "grid",
                styles: {
                    fontSize: 9,
                    cellPadding: 4,
                    overflow: "linebreak",
                    valign: "top",
                },
                headStyles: { fontStyle: "bold" },
                columnStyles: {
                    0: { cellWidth: 150, fontStyle: "bold" },
                    1: { cellWidth: contentWidth - 150 },
                },
            });

            y = doc.lastAutoTable.finalY + 10;
        }

        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 90, pageHeight - 14);
        }

        doc.save(`${safeFileName || "course-audit-report"}.pdf`);
    }

    const result = payload?.result;

    return (
        <div
            style={{
                maxWidth: 1040,
                margin: "32px auto",
                display: "grid",
                gap: 16,
                padding: "0 16px 24px",
                fontFamily: "Inter, Arial, sans-serif",
                color: "#111827",
            }}
        >
            <div
                style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 20,
                    padding: 24,
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                }}
            >
                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>
                        Upload a Blackboard course export ZIP and a rubric PDF to generate an evaluation report.
                    </div>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 18,
                    }}
                >
                    <div
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 16,
                            padding: 16,
                            background: "#ffffff",
                        }}
                    >
                        <label style={{ display: "block", fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
                            Blackboard Course Export ZIP
                        </label>
                        <input
                            type="file"
                            accept=".zip,application/zip"
                            onChange={(e) => setCourseZip(e.target.files?.[0] || null)}
                        />
                        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                            Upload the exported Blackboard course package as a ZIP file.
                        </div>
                    </div>

                    <div
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 16,
                            padding: 16,
                            background: "#ffffff",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                flexWrap: "wrap",
                                marginBottom: 10,
                            }}
                        >
                            <label style={{ display: "block", fontWeight: 700, fontSize: 15 }}>
                                Rubric PDF
                            </label>
                            <a
                                href={defaultRubricPdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#2563eb",
                                    textDecoration: "none",
                                }}
                            >
                                Download rubric PDF
                            </a>
                        </div>
                        <input
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={(e) => setRubricPdf(e.target.files?.[0] || null)}
                        />
                        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                            Use the provided rubric or upload a different PDF.
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        marginTop: 18,
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        padding: 16,
                        background: "#ffffff",
                    }}
                >
                    <label style={{ display: "block", fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
                        Additional Instructions (optional)
                    </label>
                    <textarea
                        rows={5}
                        value={extraInstructions}
                        onChange={(e) => setExtraInstructions(e.target.value)}
                        placeholder="Example: emphasize accessibility, adaptive release, and clarity of weekly modules"
                        style={{
                            width: "100%",
                            boxSizing: "border-box",
                            borderRadius: 12,
                            border: "1px solid #d1d5db",
                            padding: 12,
                            fontSize: 14,
                            lineHeight: 1.5,
                            resize: "vertical",
                            outline: "none",
                        }}
                    />
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 18 }}>
                    <button
                        onClick={runAudit}
                        disabled={!canSubmit}
                        style={{
                            padding: "12px 18px",
                            borderRadius: 12,
                            border: "1px solid #2563eb",
                            background: canSubmit ? "#2563eb" : "#93c5fd",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: canSubmit ? "pointer" : "not-allowed",
                            boxShadow: canSubmit ? "0 6px 16px rgba(37, 99, 235, 0.25)" : "none",
                        }}
                    >
                        {busy ? "Processing..." : "Analyze Course"}
                    </button>

                    {result && (
                        <button
                            onClick={downloadJson}
                            style={{
                                padding: "12px 16px",
                                borderRadius: 12,
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            Download JSON
                        </button>
                    )}

                    {result && (
                        <button
                            onClick={downloadPdf}
                            style={{
                                padding: "12px 16px",
                                borderRadius: 12,
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            Download PDF
                        </button>
                    )}
                </div>

                {statusText && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #bfdbfe",
                            background: "#eff6ff",
                            color: "#1e3a8a",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {statusText}
                    </div>
                )}

                {error && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #fecaca",
                            background: "#fef2f2",
                            color: "#991b1b",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {error}
                    </div>
                )}
            </div>

            {result && (
                <>
                    <div
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 18,
                            padding: 18,
                            background: "#ffffff",
                            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
                        }}
                    >
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
                                {result?.metadata?.courseTitle || "(Unknown Course)"}
                            </div>
                            <div style={chipStyle}>Total: {result.totalPoints} / {result.maxPossiblePoints}</div>
                            <div style={chipStyle}>{result.percentage}%</div>
                            <div style={{ ...chipStyle, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" }}>
                                {result.ratingLabel}
                            </div>
                        </div>
                        <div style={{ marginTop: 12, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
                            {result.overallFeedback}
                        </div>
                    </div>

                    <div style={{ display: "grid", gap: 12 }}>
                        {(result.scores || []).map((s, i) => (
                            <ScoreCard key={i} s={s} />
                        ))}
                    </div>

                    {result.enrollmentInsights && (
                        <div
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 18,
                                padding: 18,
                                background: "#ffffff",
                                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
                            }}
                        >
                            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Enrollment Insights</h3>
                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    margin: 0,
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    color: "#374151",
                                }}
                            >
                                {JSON.stringify(result.enrollmentInsights, null, 2)}
                            </pre>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#374151",
};